import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import ReservationService from '~/services/ReservationService';

import { StopsellApplyPayload, StopsellApplyResponse } from '~/types/stopsell';

export function useApplyBlackoutDates(propertyId: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (payload: StopsellApplyPayload): Promise<StopsellApplyResponse> =>
      ReservationService.applyBlackoutDatesBulk(propertyId, payload),
    onSuccess: (response) => {
      const succeededCount = response.result.succeeded.length;
      const failedCount = response.result.failed.length;

      if (succeededCount > 0) {
        queryClient.invalidateQueries({ queryKey: ['blackoutDates', propertyId] });
      }

      if (failedCount === 0) {
        enqueueSnackbar('Stopsell dates applied successfully', { variant: 'success' });
        return;
      }

      const firstFailure = response.result.failed[0];
      const firstFailureMessage = firstFailure ? ` First error: ${firstFailure.error}` : '';
      const succeededLabel = `${succeededCount} stopsell date ${succeededCount === 1 ? 'range' : 'ranges'}`;
      const failedLabel = `${failedCount} ${failedCount === 1 ? 'range' : 'ranges'} failed`;
      const message =
        succeededCount > 0
          ? `Applied ${succeededLabel}. ${failedLabel}.${firstFailureMessage}`
          : `Failed to apply stopsell dates. ${failedLabel}.${firstFailureMessage}`;

      enqueueSnackbar(message, { variant: succeededCount > 0 ? 'warning' : 'error' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to apply stopsell dates: ${error.message}`, { variant: 'error' });
    },
  });
}
